import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { focusInputOnCellFocused, tabToNextCellByColumn } from 'components/AgGrid/utils';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import { findAllConstructionTypesFromObjects } from 'components/WorkOrderConstructionTypesTabSelect';
import type {
    WorkOrderChangeObjectItem,
    WorkOrderChangePriceItem,
    WorkOrderChangeSeasonData,
    WorkOrderChangeStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { destroyAgGridTableFilters, getCheckedIdsArr, isNotNil, pushIfValue } from 'utils';

import type { Column } from 'ag-grid-community';
import type { GetRowIdParams } from 'ag-grid-community/dist/types/core/interfaces/iCallbackParams';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { MatchMtrDialog } from '../../MatchMtrDialog';
import { AddMtrToWorkOrderChange } from '../AddMtrToWorkOrderChange';
import { AddRatioToWorkOrderChange } from '../AddRatioToWorkOrderChange';
import { getRowClass } from '../CellRenderers';
import {
    excludeTitleModalCancelBtnText,
    excludeTitleModalConfirmBtnText,
    excludeTitleModalConfirmText,
    excludeTitleModalTitle,
    removeTitleModalConfirmText,
    removeTitleModalTitle,
} from '../constants';
import { EditNoteModal } from '../EditNoteModal';
import { EditWorkOrderChangeMtrDialog } from '../EditWorkOrderChangeMtrDialog';
import { EditWorkOrderChangeOtherDialog } from '../EditWorkOrderChangeOtherDialog';
import { EditWorkOrderChangeOtherDistribution } from '../EditWorkOrderChangeOtherDistribution';
import { EditWorkOrderChangeRatio } from '../EditWorkOrderChangeRatio';
import { EditWorkOrderChangeUerDialog } from '../EditWorkOrderChangeUerDialog';
import { MoveVolumesBetweenSeasons } from '../MoveVolumesBetweenSeasons/MoveVolumesBetweenSeasons';

import { useWorkOrderChangeColumns, useWorkOrderChangeData } from './hooks';
import { useDeleteFromWorkOrderChange } from './hooks/useDeleteFromWorkOrderChange';
import { WorkOrderChangeMenuActionTypes, WorkOrderOtherOpEnum } from './types';

interface IWorkOrderChangeProps {
    constructionTypeId: number;
    workOrderChangeId: number;
    objectFilterData: {
        rootObjectUniqueId?: string | null;
        objectUniqueId?: string | null;
        workOrderRootObjectId: number | null | undefined;
        workOrderChangeRootObjectId: number | null | undefined;
        workOrderObjectId?: number | null;
        workOrderChangeObjectId?: number | null;
    } | null;
    costRounding: number;
    contractId: number;
    isActionsDisabled: boolean;
    statusWorkOrderChange: WorkOrderChangeStatusEnum | undefined;
    refreshTable: (objectIds: number[]) => void;
    refreshProject: () => void;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    workOrderChangeObjectsData: WorkOrderChangeObjectItem[];
    hasWorkKind: boolean | null | undefined;
}

const getRowId = (params: GetRowIdParams<WorkOrderChangePriceItem>) => String(params.data.clientId);

export const WorkOrderChangeTable = forwardRef((props: IWorkOrderChangeProps, ref: any) => {
    const {
        costRounding,
        workOrderChangeId,
        constructionTypeId,
        objectFilterData,
        statusWorkOrderChange,
        isActionsDisabled,
        contractId,
        refreshTable: refreshTableProp,
        refreshProject,
        workOrderChangeSeasons,
        workOrderChangeObjectsData,
        hasWorkKind,
    } = props;

    const [openedModal, setOpenedModal] = useState<{
        type: WorkOrderChangeMenuActionTypes;
        rowData: WorkOrderChangePriceItem;
        options?: any;
    } | null>(null);

    const openModalHandler = useCallback(
        (type: WorkOrderChangeMenuActionTypes, rowData: WorkOrderChangePriceItem, options?: any) => {
            setOpenedModal({ type, rowData, options });
        },
        [],
    );
    const closeModals = useCallback(() => {
        setOpenedModal(null);
    }, []);

    const isTableRdy = useMemo(
        () => !!(workOrderChangeId && constructionTypeId),
        [constructionTypeId, workOrderChangeId],
    );

    const tableKey = useMemo(() => {
        const baseKey = `${workOrderChangeId}_${constructionTypeId}`;

        const optionalKeys: number[] = [];

        Object.values(objectFilterData ?? {}).forEach((keyValue) => {
            pushIfValue(optionalKeys, keyValue);
        });

        return `${baseKey}_${optionalKeys.join('_')}`;
    }, [constructionTypeId, objectFilterData, workOrderChangeId]);

    const { workOrderChangePriceData, fetchedItems, totalItems, changeFilterHandler } = useWorkOrderChangeData({
        workOrderChangeId,
        constructionTypeId,
        objectFilterData,
        tableKey,
    });

    const { columns, isColumnVisible, fullSetSettingsColumns, tableSettingsConfirm, checkedRows, resetCheckedRows } =
        useWorkOrderChangeColumns({
            costRounding,
            workOrderChangeId,
            statusWorkOrderChange,
            isActionsDisabled,
            openModalHandler,
            workOrderChangeSeasons,
            constructionTypeId,
            api: ref.current?.api,
            changeFilterHandler,
            objectFilterData,
            hasWorkKind,
            refreshTableObjectsProp: refreshTableProp,
        });

    const { deleteStatus, handleDelete, deleteErr, deleteSetData, deleteData, objectIdsArrDeleteMutations } =
        useDeleteFromWorkOrderChange({
            workOrderChangeId,
            constructionTypeId,
            checkedRows,
            singleDeleteRow: openedModal?.rowData,
        });

    const checkedRowsArr = useMemo(() => getCheckedIdsArr(checkedRows), [checkedRows]);

    const { removeModalTitle, removeModalText, removeModalConfirmBtn, removeModalCancelBtn } = useMemo(() => {
        if (openedModal?.rowData?.hasAcceptedCost === false) {
            return {
                removeModalTitle: excludeTitleModalTitle,
                removeModalText: excludeTitleModalConfirmText,
                removeModalConfirmBtn: excludeTitleModalConfirmBtnText,
                removeModalCancelBtn: excludeTitleModalCancelBtnText,
            };
        }

        return {
            removeModalTitle: removeTitleModalTitle,
            removeModalText: removeTitleModalConfirmText,
        };
    }, [openedModal?.rowData?.hasAcceptedCost]);
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();
    const constructionTypes = useMemo(
        () => findAllConstructionTypesFromObjects({ workOrderChangeObjectsData }),
        [workOrderChangeObjectsData],
    );

    const refreshTable = useCallback(
        (objectIds: number[]) => {
            destroyAgGridTableFilters({
                api: ref?.current?.api,
                columns: (ref?.current?.api?.getAllDisplayedColumns() as Column[]).map(
                    (c) => c.getUserProvidedColDef()?.field ?? '',
                ),
            });

            refreshTableProp(objectIds);
        },
        [ref, refreshTableProp],
    );

    const onSuccessDelete = useCallback(() => {
        resetCheckedRows();
        refreshTable(objectIdsArrDeleteMutations);
    }, [objectIdsArrDeleteMutations, refreshTable, resetCheckedRows]);

    const moveVolumesObjectIds = useMemo<{
        workOrderObjectId: number | undefined;
        workOrderChangeObjectId: number | undefined;
    } | null>(() => {
        if (openedModal?.rowData) {
            const { workOrderObjectId, workOrderChangeObjectId, workOrderRootObjectId, workOrderChangeRootObjectId } =
                openedModal.rowData;

            if (isNotNil(workOrderObjectId) || isNotNil(workOrderChangeObjectId)) {
                return {
                    workOrderObjectId: openedModal?.rowData?.workOrderObjectId,
                    workOrderChangeObjectId: openedModal?.rowData?.workOrderChangeObjectId,
                };
            }

            return {
                workOrderObjectId: workOrderRootObjectId,
                workOrderChangeObjectId: workOrderChangeRootObjectId,
            };
        }

        return null;
    }, [openedModal?.rowData]);

    return (
        <>
            <TableTopPanel
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                fetchedItems={fetchedItems}
                totalItems={totalItems}
                isColumnVisible={isColumnVisible}
                optionalColumns={fullSetSettingsColumns}
                handleTableSettingsConfirm={tableSettingsConfirm}
                showPinColumn
                tableApi={ref?.current}
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteErr}
                    deleteSetData={deleteSetData}
                    onSuccess={onSuccessDelete}
                    handleDelete={handleDelete}
                />
            </TableTopPanel>
            <div style={{ height: '100%' }} className="WorkOrderChangePrice">
                {isTableRdy && (
                    <AgGridTable
                        ref={ref}
                        datasource={workOrderChangePriceData}
                        columnDefs={columns}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        rowClassRules={getRowClass}
                        defaultColDef={{
                            sortable: false,
                        }}
                        context={{
                            tableUniqueId: tableKey,
                        }}
                        key={tableKey}
                        tabToNextCell={tabToNextCellByColumn}
                        onCellFocused={focusInputOnCellFocused}
                        className={tableKey}
                        getRowId={getRowId}
                    />
                )}
            </div>
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editUer && (
                <EditWorkOrderChangeUerDialog
                    isOpened
                    closeModalHandler={closeModals}
                    checkedUerRow={openedModal?.rowData}
                    constructionTypeId={constructionTypeId}
                    chapter={openedModal?.rowData?.chapter}
                    refreshTable={refreshTable}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editRatio && (
                <EditWorkOrderChangeRatio
                    isModalOpened
                    closeModalHandler={closeModals}
                    ratioId={openedModal?.rowData?.entryId}
                    checkedUerRow={openedModal?.rowData}
                    refreshTable={refreshTable}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.addMtr && (
                <AddMtrToWorkOrderChange
                    isOpened
                    closeModalHandler={closeModals}
                    resetCheckedRows={closeModals}
                    checkedRow={[openedModal?.rowData] as any}
                    refreshTable={refreshTable}
                    chapter={openedModal?.rowData?.chapter}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editNote && (
                <EditNoteModal
                    data={openedModal?.rowData}
                    isOpened
                    closeModalHandler={closeModals}
                    refreshTable={refreshTable}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editMtr && (
                <EditWorkOrderChangeMtrDialog
                    isModalOpened
                    rowData={openedModal?.rowData}
                    closeModalHandler={closeModals}
                    mtrId={openedModal?.rowData?.entryId}
                    workOrderChangePriceId={openedModal?.rowData?.workOrderChangePriceId}
                    refreshTableDataHandler={refreshTable}
                    chapter={openedModal?.rowData?.chapter}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.addRatio && (
                <AddRatioToWorkOrderChange
                    isOpened
                    strategy={openedModal?.options?.strategy}
                    closeModalHandler={closeModals}
                    resetCheckedRows={closeModals}
                    checkedUerRow={openedModal?.rowData}
                    refreshTable={refreshTable}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                    contractId={contractId}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.openMoveVolumesBetweenSeasonsUer && (
                <MoveVolumesBetweenSeasons
                    onClose={closeModals}
                    workOrderChangeId={workOrderChangeId}
                    chapter={openedModal?.rowData?.chapter}
                    workOrderObjectId={moveVolumesObjectIds?.workOrderObjectId}
                    workOrderChangeObjectId={moveVolumesObjectIds?.workOrderChangeObjectId}
                    workOrderChangePriceId={openedModal?.rowData?.workOrderChangePriceId}
                    workOrderPriceId={openedModal?.rowData?.workOrderPriceId}
                    refreshTableDataHandler={refreshTable}
                    workOrderChangeSeasons={workOrderChangeSeasons}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.matchMtr && (
                <MatchMtrDialog
                    contractId={contractId}
                    onMatchApply={() => {
                        refreshTable(
                            openedModal?.rowData?.workOrderObjectId ? [openedModal?.rowData?.workOrderObjectId] : [],
                        );
                        closeModals();
                    }}
                    refreshProject={refreshProject}
                    chapter={openedModal?.rowData?.chapter}
                    workOrderStatus={statusWorkOrderChange}
                    priceListId={openedModal?.rowData?.entryId}
                    workOrderPriceId={openedModal?.rowData?.workOrderPriceId}
                    refreshTable={() => {
                        refreshTable([openedModal?.rowData?.workOrderObjectId]);
                    }}
                    isChange
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editOther && (
                <EditWorkOrderChangeOtherDialog
                    isOpened
                    closeModalHandler={closeModals}
                    contractId={contractId}
                    entryId={openedModal?.rowData?.entryId}
                    currentConstructionTypeId={constructionTypeId}
                    constructionTypes={constructionTypes}
                    refreshOtherTableHandler={() => {
                        refreshTable(openedModal?.rowData?.workOrderObjectId);
                    }}
                    workOrderOtherPriceId={openedModal?.rowData?.workOrderPriceId}
                    workOrderChangeOtherPriceId={openedModal?.rowData?.workOrderChangePriceId}
                    costRounding={costRounding}
                    isReadOnly={openedModal?.options?.strategy === WorkOrderOtherOpEnum?.view}
                />
            )}
            {openedModal?.type === WorkOrderChangeMenuActionTypes.editOtherDistribution && (
                <EditWorkOrderChangeOtherDistribution
                    workOrderChangeId={workOrderChangeId}
                    workOrderChangeOtherPriceId={openedModal?.rowData?.workOrderChangePriceId}
                    workOrderOtherPriceId={openedModal?.rowData?.workOrderPriceId}
                    defaultObjectCompositeId={`${openedModal?.rowData?.workOrderRootObjectId}_${openedModal?.rowData?.workOrderChangeRootObjectId}`}
                    currentConstructionTypeId={constructionTypeId}
                    closeEditHandler={closeModals}
                    refreshOtherTableHandler={() => {
                        refreshTable(openedModal?.rowData?.workOrderObjectId);
                    }}
                    costRounding={costRounding}
                />
            )}

            <DeleteItemFrom
                isOpened={
                    openedModal?.type === WorkOrderChangeMenuActionTypes.singleDelete ||
                    openedModal?.type === WorkOrderChangeMenuActionTypes.exclude
                }
                closeDeleteModal={closeModals}
                deleteData={deleteData}
                deleteStatus={deleteStatus}
                deleteError={deleteErr}
                deleteSetData={deleteSetData}
                handleDelete={handleDelete}
                handleSuccess={onSuccessDelete}
                modalTextHeader={removeModalTitle}
                modalTextConfirmation={removeModalText}
                modalCancelButtonText={removeModalCancelBtn}
                modalConfirmButtonText={removeModalConfirmBtn}
            />
        </>
    );
});

WorkOrderChangeTable.displayName = 'WorkOrderChangeTable';
